import { useAppStore, selectDetections } from '@/store';
import type { IngredientToken } from '@allergen-lens/shared-logic';
import { Confidence } from '@allergen-lens/shared-logic';
import styles from './IngredientTokenList.module.css';

export function IngredientTokenList() {
  const tokens = useAppStore((s) => s.tokens);
  const removeToken = useAppStore((s) => s.removeToken);
  const detections = useAppStore(selectDetections);

  // Build a map of token id → detection confidence for highlighting
  const tokenConfidence = new Map<string, Confidence>();
  for (const det of detections) {
    for (const ing of det.triggeringIngredients) {
      if (det.confidence === Confidence.Present || det.confidence === Confidence.Possible) {
        tokenConfidence.set(ing, det.confidence);
      } else if (det.confidence === Confidence.NeedsReview) {
        tokenConfidence.set(ing, Confidence.NeedsReview);
      }
    }
  }

  // Group tokens: parent tokens and their children
  const parentTokens = tokens.filter((t) => !t.parentId);
  const childMap = new Map<string, IngredientToken[]>();
  for (const t of tokens) {
    if (t.parentId) {
      const children = childMap.get(t.parentId) ?? [];
      children.push(t);
      childMap.set(t.parentId, children);
    }
  }

  const getConfidenceClass = (token: IngredientToken): string => {
    const conf = tokenConfidence.get(token.id);
    if (conf === Confidence.Present) return styles.tokenHit ?? '';
    if (conf === Confidence.Possible) return styles.tokenPossible ?? '';
    if (conf === Confidence.NeedsReview) return styles.tokenReview ?? '';
    return '';
  };

  return (
    <div className={styles.tokenList}>
      {parentTokens.map((token) => {
        const children = childMap.get(token.id);
        const confClass = getConfidenceClass(token);

        return (
          <div key={token.id} className={styles.compoundGroup}>
            <span className={`${styles.token} ${confClass}`}>
              {token.name}
              <span
                className={styles.tokenRemove}
                onClick={() => removeToken(token.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeToken(token.id); } }}
                role="button"
                aria-label={`Remove ${token.name}`}
              >
                ×
              </span>
            </span>
            {children && children.length > 0 && (
              <div className={styles.compoundChildren}>
                {children.map((child) => (
                  <span key={child.id} className={`${styles.token} ${styles.tokenChild} ${getConfidenceClass(child)}`}>
                    {child.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
